import React, { useEffect, useMemo, useState, useCallback } from "react";
import { getIdToken } from "../../firebase";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faXmark, faSortAmountDown, faSortAmountUp, faEye } from "@fortawesome/free-solid-svg-icons";
import "./css/DiarioTrading.css";
// import json_local from "./json-trades-local.json";
import { hideLoading, showLoading } from "../../../redux/actions/loadingActions";
import { useDispatch } from "react-redux";
import { createPortal } from "react-dom";

// =========================================================
// Tipos y utilidades
// =========================================================

type Trade = {
    id_trade?: string;
    fecha?: string;
    fecha_iso?: string;
    par?: string;
    bias_dia?: string;
    sl_tp_be?: string; // "SL", "TP" o "BE"
    ratio?: number | null;
    cuenta?: number | null; // tamaño de cuenta en € en ese momento
    resultado_money?: number | null; // PnL en €
    resultado_pct?: number | null; // puntos porcentuales (0.98 => 0.98%) [no se usa en KPI, preferimos eff]
    emociones?: string;
    aprendizaje?: string;
    comentarios?: string;
    tv_link?: string;
    image_url?: string; // resuelto por el proxy (og:image)
    [k: string]: any;
};

const API = "/api/trades";

function fmtFecha(s?: string) {
	if (!s) return "—";

	// Esperamos formato DD/MM/YYYY
	const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
	if (!m) return s;

	const [, dd, mm, yyyy] = m;

	// Mes en JS es 0-based
	const d = new Date(
		Number(yyyy),
		Number(mm) - 1,
		Number(dd)
	);

	if (isNaN(d.getTime())) return s;

	return d.toLocaleDateString("es-ES", {
		year: "numeric",
		month: "short",
		day: "2-digit",
	});
}

function fmtMoney(n?: number | null) {
    if (n == null || isNaN(n as any)) return "—";
    try {
        return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n);
    } catch {
        return `${n} €`;
    }
}

function fmtPct(n?: number | null) {
    if (n == null || isNaN(n as any)) return "—";
    return `${Number(n).toFixed(2)}%`;
}

function pctDeResultado(r: Trade): number | null {
    if (r.resultado_money != null && r.cuenta) {
        return (Number(r.resultado_money) / Number(r.cuenta)) * 100; // % sobre la cuenta
    }
    return null;
}

function toTimeMsFecha(r: Trade): number {
    const s = (r.fecha_iso as string) || (r.fecha as string) || "";
    if (!s) return 0;
    const t = Date.parse(s);
    if (!Number.isNaN(t)) return t;
    // eslint-disable-next-line no-useless-escape
    const m = s.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/);
    if (m) {
        const d = Number(m[1]), mo = Number(m[2]), yRaw = m[3];
        const y = yRaw.length === 2 ? Number(`20${yRaw}`) : Number(yRaw);
        return new Date(y, mo - 1, d).getTime();
    }
    return 0;
}

function idNum(id?: string): number | null {
    const digits = String(id || "").replace(/\D+/g, "");
    if (!digits) return null;
    return parseInt(digits, 10);
}

const Badge: React.FC<{ children: React.ReactNode; variant?: string }> = ({ children, variant = "secondary" }) => (
    <span className={`badge text-bg-${variant} me-2`}>{children}</span>
);

type Orden = "fecha_desc" | "fecha_asc";
type ResFilter = "TODOS" | "WINS" | "LOSSES";

// =========================================================
// Modal de Detalle (sin dependencias externas)
// =========================================================

const TradeModal: React.FC<{
    trade: Trade | null;
    onClose: () => void;
}> = ({ trade, onClose }) => {
    const [zoom, setZoom] = useState(false);

    const escHandler = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
        if (zoom) setZoom(false);   // primero cierra el zoom
        else onClose();             // si no hay zoom, cierra el modal
    }
    }, [onClose, zoom]);

    useEffect(() => { if (!trade) setZoom(false); }, [trade]);

    useEffect(() => {
        if (!trade) return;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", escHandler);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", escHandler);
        };
    }, [trade, escHandler]);

    if (!trade) return null;

    const effPct = pctDeResultado(trade);
    const isBE = effPct != null && effPct > 0 && effPct < 0.05;
    const isWin = !isBE && Number(trade.resultado_money ?? trade.ratio ?? trade.resultado_pct ?? 0) > 0;
    const isLoss = !isBE && Number(trade.resultado_money ?? trade.ratio ?? trade.resultado_pct ?? 0) < 0;

    const badgeText = isBE
        ? "BE"
        : effPct != null
        ? fmtPct(effPct)
        : trade.ratio != null
        ? `${trade.ratio}R`
        : trade.resultado_money != null
        ? fmtMoney(trade.resultado_money)
        : "";

    return (
        <div className="dt-modal-backdrop" onClick={onClose}>
            <div className="dt-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                {/* X fija en esquina superior derecha */}
                <button
                    type="button"
                    className="btn btn-light btn-sm rounded-circle dt-modal-close"
                    onClick={onClose}
                    aria-label="Cerrar"
                    title="Cerrar"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>

                <div className="d-flex justify-content-between align-items-start mb-3 result-modal">
                    <div>
                        <h5 className="mb-1">{trade.par || "—"}</h5>
                        <div className="small text-muted">
                            {fmtFecha(trade.fecha_iso || trade.fecha)} / <span className="fw-semibold">{trade.id_trade || "—"}</span>
                        </div>
                    </div>
                    {badgeText && (
                        <span className={`badge ${isWin ? "text-bg-success" : isLoss ? "text-bg-danger" : "text-bg-secondary"}`}>{badgeText}</span>
                    )}
                </div>

                {trade.image_url && (
                    <>
                        <div className="ratio ratio-16x9 mb-3">
                        <img
                            src={String(trade.image_url)}
                            className="object-fit-cover dt-modal-image"
                            alt={`${trade.par ?? ""} ${fmtFecha(trade.fecha_iso || trade.fecha)}`}
                            onDoubleClick={() => setZoom(true)}
                            draggable={false}
                            style={{ cursor: "zoom-in" }}
                        />
                        </div>

                        {zoom && createPortal(
                        <div
                            onClick={() => setZoom(false)}
                            style={{
                            position: "fixed",
                            inset: 0,
                            backgroundColor: "rgba(0,0,0,0.7)",
                            zIndex: 2100,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            }}
                        >
                            <img
                            src={String(trade.image_url)}
                            alt={`${trade.par ?? ""} ${fmtFecha(trade.fecha_iso || trade.fecha)}`}
                            draggable={false}
                            style={{
                                maxWidth: "100vw",
                                maxHeight: "90vh",
                                width: "auto",
                                height: "auto",
                                objectFit: "contain",
                                cursor: "zoom-out",
                                boxShadow: "0 12px 40px rgba(0,0,0,.35)",
                            }}
                            />
                        </div>,
                        document.body
                        )}
                    </>
                )}

                <div className="mb-3">
                    {trade.bias_dia && <Badge>{trade.bias_dia}</Badge>}
                    {trade.sl_tp_be && (
                        <Badge
                            variant={
                                trade.sl_tp_be.toUpperCase() === "TP"
                                    ? "success"
                                    : trade.sl_tp_be.toUpperCase() === "SL"
                                    ? "danger"
                                    : "secondary"
                            }
                        >
                            {trade.resultado_money != null ? fmtMoney(trade.resultado_money) : "—"}
                        </Badge>
                    )}
                    {trade.ratio != null && <Badge variant="info">{trade.ratio}RR</Badge>}
                    {trade.cuenta != null && <Badge variant="warning">{trade.cuenta} €</Badge>}
                </div>

                {/* Bloques de texto completos (sin clamps) */}
                {trade.emociones && (
                    <div className="mb-3">
                        <div className="fw-semibold mb-1">Emociones:</div>
                        <p className="mb-0 white-space-prewrap">{trade.emociones}</p>
                    </div>
                )}
                {trade.aprendizaje && (
                    <div className="mb-3">
                        <div className="fw-semibold mb-1">Aprendizaje:</div>
                        <p className="mb-0 white-space-prewrap">{trade.aprendizaje}</p>
                    </div>
                )}
                {trade.comentarios && (
                    <div className="mb-2">
                        <div className="fw-semibold mb-1">Comentarios:</div>
                        <p className="mb-0 white-space-prewrap">{trade.comentarios}</p>
                    </div>
                )}
                {/* Sin enlaces ni botón de cerrar en el pie del modal */}
            </div>
        </div>
    );
};

// =========================================================
// Tarjeta Resumen (feed)
// =========================================================

const TradeCard: React.FC<{
    r: Trade;
    idKey: string;
    isFull: boolean;
    onToggleFull: (id: string) => void;
    onExitFull: () => void;
    onOpen: (t: Trade) => void;
}> = ({ r, idKey, isFull, onToggleFull, onExitFull, onOpen }) => {
    const numericForSign = (r.resultado_money ?? r.ratio ?? r.resultado_pct ?? 0) as number;
    const effPct = pctDeResultado(r);
    const isBE = effPct != null && effPct > 0 && effPct < 0.05;
    const isWin = !isBE && Number(numericForSign) > 0;
    const isLoss = !isBE && Number(numericForSign) < 0;
    const isFullScreen = isFull;
    const disableFullScreen = onExitFull;
    const toggleFullScreen = onToggleFull;

    const badgeText = isBE
        ? "BE"
        : effPct != null
        ? fmtPct(effPct)
        : r.ratio != null
        ? `${r.ratio}R`
        : r.resultado_money != null
        ? fmtMoney(r.resultado_money)
        : "";

    const stripeClass = isWin ? "card-stripe--win" : isLoss ? "card-stripe--loss" : "card-stripe--flat";

    return (
        <div className={`card h-100 card-elevated card-bordered card-stripe ${stripeClass}`}>
            {/* Overlay de fondo cuando está fullscreen (click para salir) */}
            {isFull && <div className="dt-fullscreen-overlay" onClick={onExitFull} />}

            {/* Imagen 16:9 con doble click para entrar y click para salir */}
            {r.image_url && (
                <>
                    {/* Imagen normal en la tarjeta */}
                    <div className="ratio ratio-16x9">
                    <img
                        src={String(r.image_url)}
                        className="card-img-top object-fit-cover"
                        alt={`${r.par ?? ""} ${fmtFecha(r.fecha_iso || r.fecha)}`}
                        onDoubleClick={() => toggleFullScreen(idKey)}
                        draggable={false}
                        style={{ cursor: "zoom-in" }}
                    />
                    </div>

                    {/* Imagen fullscreen vía portal (fuera del flujo de la tarjeta) */}
                    {isFullScreen &&
                        createPortal(
                            <div
                            onClick={disableFullScreen}
                            style={{
                                position: "fixed",
                                inset: 0,
                                backgroundColor: "rgba(0,0,0,0.7)",
                                zIndex: 2000,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                            >
                            <img
                                src={String(r.image_url)}
                                alt={`${r.par ?? ""} ${fmtFecha(r.fecha_iso || r.fecha)}`}
                                draggable={false}
                                style={{
                                maxWidth: "100vw",
                                maxHeight: "90vh",
                                width: "auto",
                                height: "auto",
                                objectFit: "contain",
                                cursor: "zoom-out",
                                boxShadow: "0 12px 40px rgba(0,0,0,.35)",
                                }}
                            />
                            </div>,
                            document.body
                        )
                    }
                </>
            )}

            <div className="card-body d-flex flex-column">
                {/* Encabezado compacto */}
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                        <h6 className="card-title mb-1">{r.par || "—"}</h6>
                        <div className="small text-muted">
                            {fmtFecha(r.fecha_iso || r.fecha)} / <span className="fw-semibold">{r.id_trade || "—"}</span>
                        </div>
                    </div>
                    {badgeText && (
                        <span className={`badge ${isWin ? "text-bg-success" : isLoss ? "text-bg-danger" : "text-bg-secondary"}`}>{badgeText}</span>
                    )}
                </div>

                {/* Badges clave */}
                <div className="mb-2">
                    {r.bias_dia && <Badge>{r.bias_dia}</Badge>}
                    {r.sl_tp_be && (
                        <Badge
                            variant={
                                r.sl_tp_be.toUpperCase() === "TP" ? "success" : r.sl_tp_be.toUpperCase() === "SL" ? "danger" : "secondary"
                            }
                        >
                            {r.resultado_money != null ? fmtMoney(r.resultado_money) : "—"}
                        </Badge>
                    )}
                    {r.ratio != null && <Badge variant="info">{r.ratio}RR</Badge>}
                    {r.cuenta != null && <Badge variant="warning">{r.cuenta} €</Badge>}
                </div>

                {/* Acciones: apilado en móvil, 50/50 en ≥sm; alineación vertical idéntica */}
                <div className="mt-auto">
                    <div className="row g-2 align-items-stretch actions-row">
                        <div className="col-12 col-sm-8 col-xxl-6">
                        <button
                            type="button"
                            className="btn color-principal btn-sm w-100 h-100 d-flex justify-content-center align-items-center gap-2 my-0"
                            onClick={() => onOpen(r)}
                        >
                            <FontAwesomeIcon icon={faEye} />
                            <span>Ver detalles</span>
                        </button>
                        </div>

                        <div className="col-12 col-sm-4 col-xxl-6">
                        {r.tv_link && (
                            <a
                            className="btn btn-outline-dark btn-sm w-100 h-100 d-flex justify-content-center align-items-center gap-2"
                            href={String(r.tv_link)}
                            target="_blank"
                            rel="noreferrer"
                            >
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            <span className="tv-text">TradingView</span>
                            </a>
                        )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// Página DiarioTrading
// =========================================================

const DiarioTrading: React.FC = () => {
    const dispatch = useDispatch();
    const [rows, setRows] = useState<Trade[]>([]);

    // Filtros/orden
    const [q, setQ] = useState("");
    const [parFilter, setParFilter] = useState<string>("TODOS");
    const [orden, setOrden] = useState<Orden>("fecha_asc");
    const [resFilter, setResFilter] = useState<ResFilter>("TODOS");

    // Modal detalle
    const [selected, setSelected] = useState<Trade | null>(null);

    // Fullscreen (doble click en imagen del feed)
    const [fullId, setFullId] = useState<string | null>(null);
    const disableFullScreen = useCallback(() => setFullId(null), []);
    const toggleFullScreen = useCallback((id: string) => setFullId((prev) => (prev === id ? null : id)), []);

    useEffect(() => {
        // Bloquear scroll cuando hay imagen fullscreen
        if (fullId) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
    }, [fullId]);

    useEffect(() => {
        (async () => {
            dispatch(showLoading());
            try {
                const token = await getIdToken();
                const res = await fetch(API, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) console.error("Error status", res.status);
                const json = await res.json(); // await res.json();
                setRows(json.items || []);
            } catch (e) {
                console.error(e);
            } finally {
                dispatch(hideLoading());
            }
        })();
    }, [dispatch]);

    const pares = useMemo(() => {
        const set = new Set<string>();
        rows.forEach((r) => r.par && set.add(String(r.par)));
        return ["TODOS", ...Array.from(set).sort()];
    }, [rows]);

    const filtered = useMemo(() => {
        let out = rows.filter((r) => {
            const hasDate = !!(r.fecha_iso || r.fecha);
            const hasPar = !!(r.par && String(r.par).trim() !== "");
            const hasImage = !!(r.image_url && String(r.image_url).trim() !== "");
            if (!hasDate || !hasPar || !hasImage) return false;

            const hayQ =
                !q ||
                [r.id_trade, r.par, r.bias_dia, r.sl_tp_be, r.emociones, r.aprendizaje, r.comentarios]
                    .filter(Boolean)
                    .some((v) => String(v).toLowerCase().includes(q.toLowerCase()));

            const hayPar = parFilter === "TODOS" || r.par === parFilter;
            const signValue = (r.resultado_money ?? r.ratio ?? r.resultado_pct ?? 0) as number;
            const pasaRes =
                resFilter === "TODOS" ||
                (resFilter === "WINS" && signValue > 0) ||
                (resFilter === "LOSSES" && signValue < 0);

            return hayQ && hayPar && pasaRes;
        });

        out.sort((a, b) => {
            const da = toTimeMsFecha(a);
            const db = toTimeMsFecha(b);
            if (da !== db) return orden === "fecha_desc" ? db - da : da - db;
            const ida = idNum(a.id_trade) ?? 0;
            const idb = idNum(b.id_trade) ?? 0;
            return orden === "fecha_desc" ? idb - ida : ida - idb;
        });

        return out;
    }, [rows, q, parFilter, orden, resFilter]);

    // KPIs sobre el conjunto filtrado
    const kpis = useMemo(() => {
        const total = filtered.length;
        let wins = 0, losses = 0, bes = 0;
        let pnlEur = 0;
        let pnlPct = 0; // suma de % efectivos

        filtered.forEach((r) => {
            const eff = pctDeResultado(r); // % sobre la cuenta
            if (eff != null) {
                if (eff > 0 && eff < 0.05) bes++;
                else if (eff >= 0.05) wins++;
                else if (eff < 0) losses++;
                pnlPct += eff;
            }
            pnlEur += Number(r.resultado_money ?? 0);
        });

        const wr = wins + losses ? (wins / (wins + losses)) * 100 : 0;
        return { total, wins, losses, bes, wr, pnlEur, pnlPct };
    }, [filtered]);

    return (
        <div className="container-fluid">
            <div className="text-center">
                <h2 className="pb-3 page-heading">Diario Trading</h2>
            </div>

            {/* KPIs */}
            <div className="kpi-bar d-flex flex-wrap gap-2">
                <div className="kpi">
                    <div className="kpi-title">Trades</div>
                    <div className="kpi-value">{kpis.total}</div>
                </div>
                <div className="kpi">
                    <div className="kpi-title">Win rate</div>
                    <div className="kpi-value">{kpis.wr.toFixed(1)}%</div>
                </div>
                <div className="kpi">
                    <div className="kpi-title">PnL (€)</div>
                    <div className={`kpi-value ${kpis.pnlEur >= 0 ? "text-success" : "text-danger"}`}>{fmtMoney(kpis.pnlEur)}</div>
                </div>
                <div className="kpi">
                    <div className="kpi-title">PnL (%)</div>
                    <div className={`kpi-value ${kpis.pnlPct >= 0 ? "text-success" : "text-danger"}`}>{fmtPct(kpis.pnlPct)}</div>
                </div>
            </div>

            {/* Filtros */}
            <div className="row g-2 mb-1 bg-body pt-2 pb-3 sticky-filter">
                <div className="col-12 col-md-4 col-lg-3">
                    <input className="form-control" placeholder="Buscar (ID, par, bias, notas)…" value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                    <select className="form-select" value={parFilter} onChange={(e) => setParFilter(e.target.value)}>
                        {pares.map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                    <button
                        type="button"
                        className={`btn btn-outline-secondary w-100 ${orden === "fecha_desc" ? "btn-mobile-tight" : ""}`}
                        onClick={() => setOrden((prev) => (prev === "fecha_desc" ? "fecha_asc" : "fecha_desc"))}
                        title={orden === "fecha_desc" ? "Fecha descendente" : "Fecha ascendente"}
                        aria-label="Cambiar orden por fecha"
                    >
                        <FontAwesomeIcon icon={orden === "fecha_desc" ? faSortAmountDown : faSortAmountUp} className="me-1" />
                        {orden === "fecha_desc" ? "Fecha descendente" : "Fecha ascendente"}
                    </button>
                </div>
                <div className="col-12 col-lg-3">
                    <div className="btn-group d-flex d-md-inline-flex" role="group" aria-label="Filtro resultado">
                        <button className={`btn btn-sm flex-fill ${resFilter === "TODOS" ? "color-principal" : "color-principal-outline"}`} onClick={() => setResFilter("TODOS")}>Todos</button>
                        <button className={`btn btn-sm flex-fill ${resFilter === "WINS" ? "btn-success" : "btn-outline-success"}`} onClick={() => setResFilter("WINS")}>Wins</button>
                        <button className={`btn btn-sm flex-fill ${resFilter === "LOSSES" ? "btn-danger" : "btn-outline-danger"}`} onClick={() => setResFilter("LOSSES")}>Losses</button>
                    </div>
                </div>
            </div>

            {filtered.length === 0 && <div className="text-muted">Sin resultados.</div>}

            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3">
                {filtered.map((r, i) => {
                    const idKey = r.id_trade || String(i);
                    const isFull = fullId === idKey;
                    return (
                        <div className="col" key={`${r.id_trade || "noid"}-${i}`}>
                            <TradeCard
                                r={r}
                                idKey={idKey}
                                isFull={isFull}
                                onToggleFull={toggleFullScreen}
                                onExitFull={disableFullScreen}
                                onOpen={setSelected}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Modal */}
            <TradeModal trade={selected} onClose={() => setSelected(null)} />
        </div>
    );
};

export default DiarioTrading;
